/**
 * Query-key factory (S16479.12 D4). One entry per domain that has an actual
 * endpoint consumed by a migrated hook today — no speculative keys.
 *
 * Pattern per domain: `all` (base tag for broad invalidation), plus
 * `list()` / `detail(id)` / operation-specific helpers that all extend
 * `all` so `invalidateQueries({ queryKey: domain.all })` catches everything
 * under that domain.
 */

export interface PaginationParams {
  page: number;
  pageSize: number;
  q?: string;
}

export const queryKeys = {
  projects: {
    all: ["projects"] as const,
    list: () => [...queryKeys.projects.all, "list"] as const,
    paginated: (params: PaginationParams) =>
      [...queryKeys.projects.all, "paginated", params] as const,
    stats: (slug: string) =>
      [...queryKeys.projects.all, "stats", slug] as const,
    activity: (slug: string, params: { limit: number }) =>
      [...queryKeys.projects.all, "activity", slug, params] as const,
    repositories: (projectId: string) =>
      [...queryKeys.projects.all, "repositories", projectId] as const,
    memberships: (projectId: string, params: PaginationParams) =>
      [...queryKeys.projects.all, "memberships", projectId, params] as const,
    pipelines: (slug: string, filters: Record<string, string>) =>
      [...queryKeys.projects.all, "pipelines", slug, filters] as const,
    pipelineRun: (runId: string) =>
      [...queryKeys.projects.all, "pipelines", "run", runId] as const,
    pipelineGates: (runId: string) =>
      [...queryKeys.projects.all, "pipelineGates", runId] as const,
  },
  repositories: {
    all: ["repositories"] as const,
    detail: (repoId: string) =>
      [...queryKeys.repositories.all, "detail", repoId] as const,
  },
  organizations: {
    all: ["organizations"] as const,
    current: (orgId: string) =>
      [...queryKeys.organizations.all, "current", orgId] as const,
  },
  orgProducts: {
    all: ["orgProducts"] as const,
    list: (orgId: string) =>
      [...queryKeys.orgProducts.all, "list", orgId] as const,
  },
  members: {
    all: ["members"] as const,
    list: () => [...queryKeys.members.all, "list"] as const,
    paginated: (params: PaginationParams) =>
      [...queryKeys.members.all, "paginated", params] as const,
  },
  apiKeys: {
    all: ["apiKeys"] as const,
    mine: () => [...queryKeys.apiKeys.all, "mine"] as const,
    paginated: (params: PaginationParams) =>
      [...queryKeys.apiKeys.all, "paginated", params] as const,
  },
  cartridges: {
    all: ["cartridges"] as const,
    installed: () => [...queryKeys.cartridges.all, "installed"] as const,
    installedPaginated: (params: PaginationParams) =>
      [...queryKeys.cartridges.all, "installedPaginated", params] as const,
    detail: (name: string) =>
      [...queryKeys.cartridges.all, "detail", name] as const,
    assignments: (name: string) =>
      [...queryKeys.cartridges.all, "assignments", name] as const,
    catalog: (params: { keyword?: string; limit: number; offset: number }) =>
      [...queryKeys.cartridges.all, "catalog", params] as const,
    forProject: (projectId: string) =>
      [...queryKeys.cartridges.all, "forProject", projectId] as const,
  },
  governance: {
    all: ["governance"] as const,
    artifacts: (
      repoId: string,
      params: { limit: number; offset: number },
    ) =>
      [...queryKeys.governance.all, "artifacts", repoId, params] as const,
  },
  metrics: {
    all: ["metrics"] as const,
    dashboard: (scope: string = "org") =>
      [...queryKeys.metrics.all, "dashboard", scope] as const,
    kpiTrend: (weeks: number, projectSlug: string | null = null) =>
      [...queryKeys.metrics.all, "kpiTrend", weeks, projectSlug] as const,
    adoption: (scope: string = "org") =>
      [...queryKeys.metrics.all, "adoption", scope] as const,
    tokenCost: (
      projectSlug: string,
      groupBy: string,
      filters: { since?: string; until?: string },
    ) =>
      [
        ...queryKeys.metrics.all,
        "tokenCost",
        projectSlug,
        groupBy,
        filters,
      ] as const,
    toolCost: (
      projectSlug: string | null,
      filters: { since?: string; until?: string },
    ) =>
      [...queryKeys.metrics.all, "toolCost", projectSlug, filters] as const,
    orgTokenCost: (
      groupBy: string,
      filters: { since?: string; until?: string },
    ) =>
      [...queryKeys.metrics.all, "orgTokenCost", groupBy, filters] as const,
    patternHealth: (projectSlug: string) =>
      [...queryKeys.metrics.all, "patternHealth", projectSlug] as const,
    storyCost: (storyRef: string) =>
      [...queryKeys.metrics.all, "storyCost", storyRef] as const,
    storyCostEvents: (filters: { eventType?: string; limit?: number }) =>
      [...queryKeys.metrics.all, "storyCostEvents", filters] as const,
    calibration: (projectSlug: string) =>
      [...queryKeys.metrics.all, "calibration", projectSlug] as const,
  },
  memory: {
    all: ["memory"] as const,
    meta: (projectSlug: string) =>
      [...queryKeys.memory.all, "meta", projectSlug] as const,
    graphSeed: (projectSlug: string) =>
      [...queryKeys.memory.all, "graphSeed", projectSlug] as const,
  },
  onboarding: {
    all: ["onboarding"] as const,
    status: () => [...queryKeys.onboarding.all, "status"] as const,
  },
  integrations: {
    all: ["integrations"] as const,
    status: () => [...queryKeys.integrations.all, "status"] as const,
  },
  identity: {
    all: ["identity"] as const,
    connections: (email: string) =>
      [...queryKeys.identity.all, "connections", email] as const,
  },
  license: {
    all: ["license"] as const,
    current: () => [...queryKeys.license.all, "current"] as const,
  },
  partners: {
    all: ["partners"] as const,
    pending: () => [...queryKeys.partners.all, "pending"] as const,
  },
  workState: {
    all: ["workState"] as const,
    detail: (
      projectSlug: string,
      filters: { since?: string; limit?: number },
    ) => [...queryKeys.workState.all, "detail", projectSlug, filters] as const,
  },
  artifacts: {
    all: ["artifacts"] as const,
    list: (params?: { project_id?: string; limit?: number; offset?: number }) =>
      [...queryKeys.artifacts.all, "list", params] as const,
    detail: (id: string) =>
      [...queryKeys.artifacts.all, "detail", id] as const,
  },
  audit: {
    all: ["audit"] as const,
    list: (filters: {
      projectId?: string;
      workItemRef?: string;
      eventKind?: string;
      runId?: string;
      since?: string;
      until?: string;
    }) => [...queryKeys.audit.all, "list", filters] as const,
  },
} as const;
